import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/authContext"
import { LockClosedIcon, ArrowPathIcon } from "@heroicons/react/24/outline"


const ChangePasswordForm: React.FC = () => {
  const auth = useAuth()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = (): boolean => {
    const { oldPassword, newPassword, confirmPassword } = formData

    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("All fields are required")
      return false
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long")
      return false
    }

    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match")
      return false
    }

    if (oldPassword === newPassword) {
      setError("New password must be different from the old password")
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setError(null)
    setLoading(true)

    try {
      const { oldPassword, newPassword } = formData

      await new Promise<void>((resolve, reject) => {
        auth?.changePassword( { oldPassword, newPassword },

          (res) => {
            setLoading(false)
            setSuccess("Password changed successfully! Redirecting to dashboard...")

            setTimeout(() => {
              navigate("/")
              resolve()
            }, 1500)
          },
          (err) => {
            setLoading(false)

            let errorMessage = "Failed to change password. Please try again."

            if (err && typeof err === "object") {
              const anyErr = err as any

              errorMessage =
                anyErr?.response?.data?.message ||
                anyErr?.data?.message ||
                anyErr?.message ||
                errorMessage
            }

            setError(errorMessage)
            reject(errorMessage)
          }

        )
      })
    } catch (error) {
      console.error("Password change error:", error)
      if (!error) {
        setError("An unexpected error occurred. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (

    <div className="max-w-md w-full space-y-8 w-full">
      <div>

        <p className="mt-2 text-left text-xs text-gray-600">
          Please enter your current password and set a new one.
        </p>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="rounded-md shadow-sm -space-y-px">
          <div>
            <label htmlFor="oldPassword" className="sr-only">Current Password</label>
            <input
              id="oldPassword"
              name="oldPassword"
              type="password"
              required
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm dark:bg-slate-700
 dark:text-white
 dark:border-gray-500 dark:focus:placeholder:text-gray-300"
              placeholder="Current Password"
              value={formData.oldPassword}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="newPassword" className="sr-only">New Password</label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              required
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm  dark:bg-slate-700
 dark:text-white
 dark:border-gray-500 dark:focus:placeholder:text-gray-300"
              placeholder="New Password"
              value={formData.newPassword}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="sr-only">Confirm New Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm dark:focus:placeholder:text-gray-300
 dark:bg-slate-700
 dark:text-white
 dark:border-gray-500 dark:focus:placeholder:text-gray-300"
              placeholder="Confirm New Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            {loading ? (
              <>
                <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />
                Changing Password...
              </>
            ) : (
              <>
                <LockClosedIcon className="h-5 w-5 text-indigo-500 group-hover:text-indigo-400 mr-2" />
                Change Password
              </>
            )}
          </button>
        </div>
      </form>
    </div>

  )
}

export default ChangePasswordForm
